import { DistanceUnit, DistanceUnitShort, exhaustiveTypeCheck, WeightUnit } from 'hevy-shared';
import { stores } from 'state/stores';
import { t } from './t';
import { secondsToWordFormatLocalized } from './units';

/**
 * Defaults to 3 decimal places
 * @param value
 * @param options https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options
 *
 * Eg: formatNumber(12123.1234, { maximumFractionDigits: 2 }) -> 12,123.12
 * @returns
 */
export const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  const userLanguage = stores.userPreferences.languageFormattedForJs;
  return Intl.NumberFormat(userLanguage, options).format(value);
};

export type Unit =
  | WeightUnit
  | DistanceUnitShort
  | DistanceUnit
  | 'm'
  | 'yd'
  | undefined
  | 'hours'
  | 'minutes'
  | 'seconds'
  | 'steps'
  | 'floors'
  | 'reps'
  | 'steps/min'
  | 'floors/min'
  | 'time/km'
  | 'time/mi';
/**
 *
 * @param value If you are using the unit time/km or time/mi, pass in the number of seconds
 * @param unit *Note: lbs, step, floor, rep, step/min, floor/min can only handle the unitDisplay: 'short' option (the default)
 * @param options https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options
 *
 * Examples in French:
 *
 * formatNumberWithUnit(12345.45, 'kg', { unitDisplay: 'long' }) -> 12 123,45 kilogrammes
 *
 * formatNumberWithUnit(12345.45, 'kg') and formatNumberWithUnit(12345.45, 'kg', { unitDisplay: 'short' }) -> 12 123,45 kg
 *
 * formatNumberWithUnit(12345.45, 'kg', { unitDisplay: 'narrow' }) -> 12 123,45kg
 * @returns
 */
export const formatNumberWithUnit = (
  value: number,
  unit: Unit,
  options?: Intl.NumberFormatOptions,
): string => {
  if (!unit) {
    return formatNumber(value, options);
  }

  switch (unit) {
    case 'lbs':
      // Using { style: 'unit', unit: 'pound' } displays the unit as 'lb' instead of 'lbs'
      return t('global.numberWithWeightUnit', {
        number: formatNumber(value, options),
        unit: t('global.lbs'),
      });
    case 'kg':
      return formatNumber(value, { ...options, style: 'unit', unit: 'kilogram' });
    case 'km':
      return formatNumber(value, { ...options, style: 'unit', unit: 'kilometer' });
    case 'mi':
      return formatNumber(value, { ...options, style: 'unit', unit: 'mile' });
    case 'kilometers':
      return formatNumber(value, {
        ...options,
        style: 'unit',
        unit: 'kilometer',
        unitDisplay: 'long',
      });
    case 'miles':
      return formatNumber(value, { ...options, style: 'unit', unit: 'mile', unitDisplay: 'long' });
    case 'm':
      return formatNumber(value, { ...options, style: 'unit', unit: 'meter' });
    case 'yd':
      return formatNumber(value, { ...options, style: 'unit', unit: 'yard' });
    case 'hours':
      return formatNumber(value, { ...options, style: 'unit', unit: 'hour' });
    case 'minutes':
      return formatNumber(value, { ...options, style: 'unit', unit: 'minute' });
    case 'seconds':
      return secondsToWordFormatLocalized(value);
    case 'steps':
      return t('global.numSteps', {
        numSteps: formatNumber(value, options),
      });
    case 'floors':
      return t('global.numFloors', {
        numFloors: formatNumber(value, options),
      });
    case 'reps':
      return t('global.numReps', {
        numReps: formatNumber(value, options),
      });
    case 'steps/min':
      return t('global.numStepsPerMin', { numSteps: formatNumber(value, options) });
    case 'floors/min':
      return t('global.numFloorsPerMin', { numFloors: formatNumber(value, options) });
    case 'time/km':
      return t('global.timePerKm', { time: secondsToWordFormatLocalized(value) });
    case 'time/mi':
      return t('global.timePerMi', { time: secondsToWordFormatLocalized(value) });
    default:
      exhaustiveTypeCheck(unit);
      return formatNumber(value, options);
  }
};

export const formatCurrency = (value: number) => {
  return formatNumber(value, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
  });
};

/**
 *
 * @param value
 * @param options
 * dateStyle and timeStyle cheat sheet: https://justinjaywang.github.io/intl.datetimeformat-cheatsheet/?locale=auto
 *
 * All options cheat sheet: https://devhints.io/wip/intl-datetime
 *
 * Examples with new Date('2026-01-12')
 *
 * {month: 'narrow'} -> J
 *
 * {month: 'short'} -> Jan
 *
 * {month: 'long'} -> January
 *
 * {month: 'numeric'} -> 1
 *
 * {month: '2-digit'} -> 01
 * @returns
 */
export const formatDateTime = (value: Date, options?: Intl.DateTimeFormatOptions): string => {
  const userLanguage = stores.userPreferences.languageFormattedForJs;
  return Intl.DateTimeFormat(userLanguage, options).format(value);
};
